/* Emoji + GIF preferences, durable per user via account_data:
     app.atrium.recent_emoji — usage counts (drives quick-react + "frequently used")
     im.ponies.user_emotes   — custom emote pack (MSC2545 shape: images.{name}.url)
     app.atrium.recent_gifs  — the last GIFs sent/reacted with
   All three merge-on-boot and persist debounced, mirroring stores/mentions.ts. */
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'

export interface CustomEmote {
  name: string // shortcode, without the colons
  url: string  // mxc://
}

export interface GifPick {
  id: string
  title: string
  url: string     // full-size — what a message/reaction carries
  preview: string // fixed-width — what pickers show
  w: number
  h: number
}

const COUNTS_TYPE = 'app.atrium.recent_emoji'
const EMOTES_TYPE = 'im.ponies.user_emotes'
const GIFS_TYPE = 'app.atrium.recent_gifs'

const COUNT_CAP = 300  // distinct emojis worth remembering
const GIF_CAP = 24
export const QUICK_DEFAULTS = ['👍', '❤️', '😂', '🎉', '👀', '🙏']

export const EMOTE_NAME_RE = /^[a-zA-Z0-9_-]{2,32}$/

interface EmojiState {
  counts: Record<string, number>
  emotes: CustomEmote[]
  recentGifs: GifPick[]
  loaded: boolean
}

let countsTimer: ReturnType<typeof setTimeout> | null = null

export const useEmoji = defineStore('emoji', {
  state: (): EmojiState => ({ counts: {}, emotes: [], recentGifs: [], loaded: false }),

  getters: {
    /** The hover bar's quick reactions: my most-used, padded with sane defaults. */
    quickReactions: (s): string[] => {
      const top = Object.entries(s.counts)
        .sort((a, b) => b[1] - a[1])
        .map(([ch]) => ch)
      const out = [...top]
      for (const d of QUICK_DEFAULTS) {
        if (out.length >= 6) break
        if (!out.includes(d)) out.push(d)
      }
      return out.slice(0, 6)
    },
    /** "Frequently used" strip at the top of the full picker. */
    frequent: (s): string[] =>
      Object.entries(s.counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 24)
        .map(([ch]) => ch),
    /** shortcode → mxc, for :name: rendering in message bodies. */
    emoteMap: (s): Record<string, string> => {
      const map: Record<string, string> = {}
      for (const e of s.emotes) map[e.name] = e.url
      return map
    },
  },

  actions: {
    async load() {
      if (this.loaded || !matrix.hasSession()) return
      this.loaded = true
      try {
        const [counts, emotes, gifs] = await Promise.all([
          matrix.getAccountData<{ counts?: Record<string, number> }>(COUNTS_TYPE),
          matrix.getAccountData<{ images?: Record<string, { url?: string }> }>(EMOTES_TYPE),
          matrix.getAccountData<{ gifs?: GifPick[] }>(GIFS_TYPE),
        ])
        if (counts?.counts) this.counts = { ...counts.counts, ...this.counts }
        if (emotes?.images) {
          this.emotes = Object.entries(emotes.images)
            .filter(([name, v]) => EMOTE_NAME_RE.test(name) && typeof v?.url === 'string' && v.url.startsWith('mxc://'))
            .map(([name, v]) => ({ name, url: v.url! }))
            .sort((a, b) => a.name.localeCompare(b.name))
        }
        if (Array.isArray(gifs?.gifs)) this.recentGifs = gifs.gifs.slice(0, GIF_CAP)
      } catch (e) {
        console.warn('[emoji] load failed', e)
        this.loaded = false // let a later open retry
      }
    },

    /** Count a use (send or react) — feeds quick-react + frequently-used. */
    noteEmoji(ch: string) {
      this.counts[ch] = (this.counts[ch] || 0) + 1
      const keys = Object.keys(this.counts)
      if (keys.length > COUNT_CAP) {
        // Drop the coldest tail so the blob can't grow without bound.
        keys.sort((a, b) => this.counts[a] - this.counts[b])
        for (const k of keys.slice(0, keys.length - COUNT_CAP)) delete this.counts[k]
      }
      if (countsTimer) clearTimeout(countsTimer)
      countsTimer = setTimeout(() => {
        countsTimer = null
        matrix.setAccountData(COUNTS_TYPE, { counts: this.counts }).catch(() => {})
      }, 800)
    },

    noteGif(g: GifPick) {
      this.recentGifs = [g, ...this.recentGifs.filter((x) => x.id !== g.id)].slice(0, GIF_CAP)
      matrix.setAccountData(GIFS_TYPE, { gifs: this.recentGifs }).catch(() => {})
    },

    /** Upload an image and add it to my emote pack as :name:. */
    async addEmote(name: string, file: File): Promise<CustomEmote> {
      const clean = name.trim().replace(/^:|:$/g, '')
      if (!EMOTE_NAME_RE.test(clean)) throw new Error('Names are 2–32 letters, digits, - or _')
      if (!file.type.startsWith('image/')) throw new Error('Emotes must be images')
      if (file.size > 1024 * 1024) throw new Error('Keep emotes under 1 MB')
      const url = await matrix.uploadMedia(file, clean)
      this.emotes = [...this.emotes.filter((e) => e.name !== clean), { name: clean, url }]
        .sort((a, b) => a.name.localeCompare(b.name))
      await this.persistEmotes()
      return { name: clean, url }
    },

    async removeEmote(name: string) {
      this.emotes = this.emotes.filter((e) => e.name !== name)
      await this.persistEmotes()
    },

    async persistEmotes() {
      const images: Record<string, { url: string; usage: string[] }> = {}
      // usage per MSC2545 — these are emoticons (inline emoji), not stickers.
      for (const e of this.emotes) images[e.name] = { url: e.url, usage: ['emoticon'] }
      await matrix.setAccountData(EMOTES_TYPE, { images })
    },
  },
})
